import { useRef, useState, useEffect } from "react"
import { downscale, dataURLToBlob } from "../utils/imageFunctions"

export default function useCameraStream({ onFile, captureIntervalMs = 500 }) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)
  const [devices, setDevices] = useState([])
  const [selectedDeviceId, setSelectedDeviceId] = useState("")
  const [isCapturing, setIsCapturing] = useState(false)
  const [intervalId, setIntervalId] = useState(null)
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      )
      setDevices(videoDevices)
      if (videoDevices.length > 0) {
        setSelectedDeviceId(videoDevices[0].deviceId)
      }
    }

    getDevices()
  }, [])

  useEffect(() => {
    const startCamera = async () => {
      try {
        const constraints = {
          video: {
            deviceId: selectedDeviceId
              ? { exact: selectedDeviceId }
              : undefined,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.onloadedmetadata = () => {
            if (canvasRef.current) {
              canvasRef.current.width = videoRef.current.videoWidth
              canvasRef.current.height = videoRef.current.videoHeight
            }
            setVideoSize({
              width: videoRef.current.videoWidth,
              height: videoRef.current.videoHeight,
            })
          }
        }
      } catch (err) {
        console.error("Error accessing the camera: ", err)
      }
    }

    if (selectedDeviceId) {
      startCamera()
    }

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks()
        tracks.forEach((track) => track.stop())
      }
    }
  }, [selectedDeviceId])

  useEffect(() => {
    if (selectedFile) {
      onFile(selectedFile, { setLoading, loadingRef })
    }
  }, [selectedFile])

  useEffect(() => {
    if (isCapturing) {
      const id = setInterval(captureFrame, captureIntervalMs)
      setIntervalId(id)
    } else if (intervalId) {
      clearInterval(intervalId)
      setIntervalId(null)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isCapturing, captureIntervalMs])

  const handleTakePhoto = (dataUri) => {
    downscale(dataUri, 1024).then((resizedImage) => {
      const blob = dataURLToBlob(resizedImage)
      let extension = blob.type.split("/")[1]
      if (extension === "jpeg") {
        extension = "jpg"
      }
      const newFile = new File([blob], `camera-image.${extension}`, {
        type: blob.type,
      })
      setSelectedFile(newFile)
    })
  }

  const captureFrame = () => {
    if (loadingRef.current) return
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d")
      context.drawImage(
        videoRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      )
      const frame = canvasRef.current.toDataURL("image/png")
      handleTakePhoto(frame)
    }
  }

  const handleDeviceChange = (event) => {
    setSelectedDeviceId(event.target.value)
  }

  const toggleCapture = () => {
    setIsCapturing((prev) => !prev)
  }

  return {
    videoRef,
    canvasRef,
    devices,
    selectedDeviceId,
    handleDeviceChange,
    isCapturing,
    toggleCapture,
    loading,
    videoSize,
  }
}
